import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom';
import { fetchGroup, removePlayerFromGroup, deactivateGroup } from '../api';
import { getPrimaryPlayerInGroup } from '../utils';
import Invite from '../components/Invite';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import { getGroupDay } from "../utils";

/**
 * Stats Page
 *
 * @param query
 * @returns {JSX.Element}
 * @constructor
 */
const Stats = (query) => {
  const params = useParams();
  const [group, setGroup] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [playerIsPrimary, setPlayerIsPrimary] = useState(false);
  const [day, setDay] = useState(0);
  const [showComeBackMsg, setShowComeBackMsg] = useState(false);

  const inviteLink = useRef('');
  const player = JSON.parse(localStorage.getItem('player'));

  useEffect(() => {
    if (!params.groupGuid) {
      window.location.href = '/dashboard';
    }

    // The stored player should always exist at this point
    if (player === null) {
      window.location = '/';
      return;
    }

    if (group === null) {
      fetchGroup(params.groupGuid)
        .then((response) => {
          console.log('player: ', player);
          console.log('group: ', response.group);
          if (!response.group) {
            window.location.href = '/dashboard';
          }

          setGroup(response.group);

          const day = getGroupDay(response.group);
          setDay(day);

          const primaryPlayer = getPrimaryPlayerInGroup(response.group);
          if (player._id === primaryPlayer._id || player.temp_id === primaryPlayer.temp_id) {
            setPlayerIsPrimary(true);
          }

          for (let i = 0; i < response.group.players.length; i++) {
            if (response.group.players[i]._id === player._id || response.group.players[i]._id === player._id) {
              if ((response.group.players[i].group_stats?.questions_answered ?? 0) === response.group.stats.questions_asked) {
                setShowComeBackMsg(true);
              }
              break;
            }
          }
        })
        .catch((err) => {
          console.error('Failed to fetch group: ', err);
          if (err.response) {
            setErrorMsg(err.response.data.error);
          } else if (err.message) {
            setErrorMsg(err.message);
          }
        });
    }

    inviteLink.current = process.env.REACT_APP_BASE_URL + '/invitation/' + player.temp_id + '/' + params.groupGuid;
  }, []);

  /**
   * onLeaveClick
   *
   * handler for the Leave Game button
   *
   * @returns {Promise<void>}
   */
  const onLeaveClick = async () => {
    let answer = window.confirm("Are you sure you want to leave this game?");
    if (answer === true) {
      const result = await removePlayerFromGroup(player, params.groupGuid);

      if (!result.success) {
        setErrorMsg(result.error);
      } else {
        window.location = '/dashboard';
      }
    }
  }

  /**
   * onShutdownClick
   *
   * handler for the Delete Game button
   *
   * @returns {Promise<void>}
   */
  const onShutdownClick = async () => {
    let answer = window.confirm("Are you sure you want to shut down this game? This is irrecoverable and all players will be ejected from the game.");
    if (answer === true) {
      const result = await deactivateGroup(params.groupGuid);

      const player = JSON.parse(localStorage.getItem('player'));
      player.groups = player.groups.filter((group) => group !== params.groupGuid);
      localStorage.setItem('player', JSON.stringify(player));

      if (!result.success) {
        setErrorMsg(result.error);
      } else if (result.success) {
        window.location = '/dashboard';
      }
    }
  }

  /**
   * onPlayerClick
   *
   * Handler for the player list item click
   *
   * @param e
   */
  const onPlayerClick = (e) => {
    if (playerIsPrimary) {
      if (e.currentTarget.classList.contains('player-stat-open')) {
        e.currentTarget.classList.remove('player-stat-open');
        e.currentTarget.classList.add('player-stat-close');
      } else if (e.currentTarget.classList.contains('player-stat-close')) {
        e.currentTarget.classList.remove('player-stat-close');
        e.currentTarget.classList.add('player-stat-open');
      } else {
        e.currentTarget.classList.add('player-stat-open');
      }
    }
  }

  /**
   * onRemovePlayer
   *
   * Handler for the remove player icon click
   *
   * @param e
   * @param playerId
   * @returns {Promise<void>}
   */
  const onRemovePlayer = async (e, playerId) => {
    if (!playerIsPrimary) {
      return;
    }

    const answer = window.confirm("Are you sure you want to remove this player from the game?");
    if (answer) {
      const result = await removePlayerFromGroup(player, params.groupGuid);

      if (!result.success) {
        setErrorMsg(result.error);
      } else if (result.success) {
        window.location.reload();
      }
    }
  }

  const getAvgScore = (player) => {
    if (player?.group_stats?.questions_answered > 0) {
      const avg = (player?.group_stats?.total_score ?? 0) / ((player?.group_stats?.questions_answered ?? 0) * process.env.REACT_APP_DAILY_QUESTION_COUNT);
      return Math.round(avg * 100);
    }

    return 0;
  }

  if (group === null) {
    return (
      <div className="page-wrap">
        <Header/>
        <div className="stats-page">
          <Loading/>
        </div>
        <Footer/>
      </div>
    )
  } else {
    return (
      <div className="page-wrap">
        <Header />
        <div className="stats-page">

          {errorMsg === '' ? null : <div className="error-msg">{errorMsg}</div>}
          <div className="stats-subject"><span className="stats-subject-text">{group.subject}</span></div>
          <div className="stats-block">
            {/*<div className="group-stat">Created on <b>{format(new Date(group.date_created), 'MM/dd/YYY')}</b></div>*/}
            <div className="group-stat">Day: <b>{day}</b></div>
            <div className="group-stat">Total Questions: <b>{group?.stats?.questions_asked + (process.env.REACT_APP_DAILY_QUESTION_COUNT - group.todays_answers.length)}</b></div>
            {/*<div className="group-stat">Total Answers: <b>{group?.stats?.questions_answered}</b></div>*/}
            {/*<div className="group-stat">Max Score: <b>{group?.stats?.questions_asked * process.env.REACT_APP_DAILY_QUESTION_COUNT}</b></div>*/}
            {/*<div className="group-stat">Average Score: <b>{2.4}</b></div>*/}
          </div>
          {showComeBackMsg &&
            <div className="come-back-msg">You're all caught up! Come back tomorrow for more!</div>
          }
          <div className="player-stats-block">
            <div className="player-stat-hint">score</div>
            {group.players.filter((player) => player.status === undefined || player.status === 'active').map((thisPlayer, index) => (
              <div key={index} className="player-stat-wrap">
                {!thisPlayer.primary &&
                <div className="delete-player-wrap">
                  <img src="/trash.svg" className="delete-player-icon" onClick={(e) => onRemovePlayer(e, thisPlayer._id ?? thisPlayer.temp_id)}/>
                </div>
                }
                <div key={index} className="player-stat" onClick={(e) => {if (!thisPlayer.primary) {onPlayerClick(e)}}}>
                  <div className="player-stat-left">
                    {thisPlayer.name ?
                      <div className="player-name">{thisPlayer.primary && <span>*</span>}{thisPlayer.name}</div>
                      : thisPlayer.temp_id === player.temp_id ?
                        <div className="player-name">You</div>
                        :
                        <div className="player-name">Anonymous</div>
                    }
                    <div className="player-unanswered">{(thisPlayer.group_stats.questions_answered ?? 0) === group.stats.questions_asked ?
                      <span>All caught up!</span>
                      :
                      <span>{group.stats.questions_asked - (thisPlayer.group_stats.questions_answered ?? 0) + (process.env.REACT_APP_DAILY_QUESTION_COUNT - group.todays_answers.length)} unanswered</span>}</div>
                  </div>
                  {(thisPlayer.group_stats.questions_answered ?? 0) === group.stats.questions_asked ?
                    <div className="player-score">
                      <span>{thisPlayer?.group_stats?.total_score ?? 0}</span>
                      <span className="player-score-total">/{(thisPlayer?.group_stats?.questions_answered ?? 0) * process.env.REACT_APP_DAILY_QUESTION_COUNT ?? 0}</span>
                      <div className="player-score-avg">{getAvgScore(thisPlayer)}%</div>
                    </div>
                    :
                    <div className="player-score player-score-dimmed">
                      <span>{thisPlayer?.group_stats?.total_score ?? 0}</span>
                      <span className="player-score-total">/{(thisPlayer?.group_stats?.questions_answered ?? 0) * process.env.REACT_APP_DAILY_QUESTION_COUNT ?? 0}</span>
                      <div className="player-score-avg">{getAvgScore(thisPlayer)}%</div>
                    </div>
                  }
                </div>
              </div>
            ))}
          </div>
          {playerIsPrimary &&
            <div className="primary-actions">
              <Invite groupGuid={params.groupGuid} subject={group.subject}/>
              <div className="link-button-wrap link-button-wrap-stats-page">
                <span className="link-button link-button-dim" onClick={onShutdownClick}>Delete Game</span>
              </div>
              {/*<button className="main-btn shutdown-btn" onClick={onShutdownClick}>Shut It Down</button>*/}
              {/*<button className="main-btn">End Game</button>*/}
              {/*<button className="main-btn">Reset Scores</button>*/}
            </div>
          }
          {!playerIsPrimary &&
            <div className="link-button-wrap link-button-wrap-stats-page">
              <span className="link-button link-button-dim" onClick={onLeaveClick}>Leave Game</span>
            </div>
          }
        </div>
        <Footer/>
      </div>
    )
  }
}

export default Stats;
